import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Base64Codec.module.css';

const toBase64Utf8 = (text: string): string => {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

const fromBase64Utf8 = (base64: string): string => {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const Base64Codec: React.FC = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const showMessage = (text: string, error = false) => {
    setMessage(text);
    setIsError(error);
    setTimeout(() => {
      setMessage('');
      setIsError(false);
    }, 2500);
  };

  const handleEncode = () => {
    if (!input) {
      showMessage(t('base64Codec.messageInputEncodeRequired'), true);
      return;
    }
    try {
      setOutput(toBase64Utf8(input));
    } catch {
      showMessage(t('base64Codec.messageEncodeFailed'), true);
    }
  };

  const handleDecode = () => {
    if (!input) {
      showMessage(t('base64Codec.messageInputDecodeRequired'), true);
      return;
    }
    try {
      setOutput(fromBase64Utf8(input.trim()));
    } catch {
      showMessage(t('base64Codec.messageDecodeFailed'), true);
    }
  };

  const handleCopy = async () => {
    if (!output) {
      showMessage(t('base64Codec.messageNoResultToCopy'), true);
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      showMessage(t('base64Codec.messageCopySuccess'));
    } catch {
      showMessage(t('base64Codec.messageCopyFailed'), true);
    }
  };

  const handleClear = () => {
    setInput('');
    setOutput('');
    setMessage('');
    setIsError(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t('base64Codec.title')}</h2>
        <p className={styles.description}>{t('base64Codec.description')}</p>

        {message && <div className={`${styles.tip} ${isError ? styles.error : styles.success}`}>{message}</div>}

        <div className={styles.label}>{t('base64Codec.inputLabel')}</div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className={styles.textarea}
          placeholder={t('base64Codec.inputPlaceholder')}
        />

        <div className={styles.actions}>
          <button onClick={handleEncode} className={styles.primaryButton}>
            {t('base64Codec.encodeButton')}
          </button>
          <button onClick={handleDecode} className={styles.primaryButton}>
            {t('base64Codec.decodeButton')}
          </button>
          <button onClick={handleCopy} className={styles.secondaryButton}>
            {t('base64Codec.copyButton')}
          </button>
          <button onClick={handleClear} className={styles.secondaryButton}>
            {t('base64Codec.clearButton')}
          </button>
        </div>

        <div className={styles.label}>{t('base64Codec.outputLabel')}</div>
        <textarea value={output} readOnly className={styles.textarea} placeholder={t('base64Codec.outputPlaceholder')} />
      </div>
    </div>
  );
};

export default Base64Codec;
